import React, { useMemo, useState, useEffect } from "react";
import AppLayout from "../components/AppLayout";
import CourseCard from "../components/CourseCard";
import { api } from "../api/client";
import { ChevronDown, Loader2, BookOpen } from "lucide-react";

const STATIC_CATEGORIES = ["All", "Cybersecurity", "Close Protection", "Travel Safety", "Tactical", "Risk Management", "Maritime"];

export default function Catalog() {
    const [active, setActive] = useState("All");
    const [level, setLevel] = useState("All");
    const [sort, setSort] = useState("Newest");
    const [courses, setCourses] = useState([]);
    const [categories, setCategories] = useState(STATIC_CATEGORIES);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/categories")
            .then((cats) => { if (cats?.length) setCategories(["All", ...cats.filter((c) => c !== "All")]); })
            .catch(() => {});
        api.get("/courses")
            .then(setCourses)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        let list = courses.slice();
        if (active !== "All") list = list.filter((c) => c.category === active);
        if (level !== "All") list = list.filter((c) => c.level === level);
        if (sort === "Price: Low to High") list.sort((a, b) => a.price - b.price);
        if (sort === "Price: High to Low") list.sort((a, b) => b.price - a.price);
        if (sort === "Top rated") list.sort((a, b) => b.rating - a.rating);
        return list;
    }, [courses, active, level, sort]);

    return (
        <AppLayout searchPlaceholder="Search for courses...">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Courses</h1>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setActive(cat)}
                            data-testid={`cat-${cat.toLowerCase().replace(/\s/g, "-")}`}
                            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                                active === cat ? "bg-white text-ink-900" : "text-gray-400 hover:text-white"
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
                <div className="flex gap-3">
                    <div className="relative">
                        <select
                            value={level}
                            onChange={(e) => setLevel(e.target.value)}
                            data-testid="filter-level"
                            className="appearance-none bg-ink-700 border border-white/10 rounded-full pl-5 pr-10 py-2.5 text-sm font-medium hover:border-white/20 focus:outline-none focus:border-lime/40 cursor-pointer"
                        >
                            <option>All</option>
                            <option>Beginner</option>
                            <option>Intermediate</option>
                            <option>Advanced</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                    <div className="relative">
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            data-testid="filter-sort"
                            className="appearance-none bg-ink-700 border border-white/10 rounded-full pl-5 pr-10 py-2.5 text-sm font-medium hover:border-white/20 focus:outline-none focus:border-lime/40 cursor-pointer"
                        >
                            <option>Newest</option>
                            <option>Top rated</option>
                            <option>Price: Low to High</option>
                            <option>Price: High to Low</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-24 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading courses…
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <BookOpen className="w-10 h-10 text-gray-600 mb-4" />
                    <p className="text-gray-400 font-semibold">No courses yet</p>
                    <p className="text-sm text-gray-600 mt-1">
                        {courses.length === 0
                            ? "Courses added via the admin panel will appear here."
                            : "No courses match your current filters."}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {filtered.map((c) => <CourseCard key={c.id} course={c} />)}
                </div>
            )}
        </AppLayout>
    );
}
